// 소득·자산(가구 단위, 단위 만원). 표는 lifeTables.economy (KOSIS 2025 통계에서 만든 값). 순수 함수이며 난수를 쓰지 않는다.
// 설계: docs/economy-design.md, docs/life-events-engine-design.md 14절.
// 단순화(게임 추정): 가구 소득 = 본인 + 배우자(본인의 spouseIncomeShare 배) + 65세 이후 공적이전. 소비는 가구 소득에서 정한다.
import { getLifeTables } from './lifeEvents.js';

export const WORK_START_AGE = 24; // jobs.json 의 firstJobAge 와 같다. 이 나이 전에는 경제 계산을 하지 않는다(부모 가구).
const RETIRE_AGE = 60;
const OCCUPATION_BY_JOB = {
  sales: 'sales', counselor: 'professional', pr: 'professional', hotel: 'service', activist: 'professional',
  writer: 'professional', chef: 'service', designer: 'professional', musician: 'professional', developer: 'professional',
  owner: 'sales', investor: 'professional', accountant: 'professional', trader: 'clerk', realtor: 'clerk',
  civil: 'clerk', police: 'service', corp: 'clerk', lawyer: 'professional', military: 'service',
  teacher: 'professional', researcher: 'professional', doctor: 'professional', editor: 'professional', clergy: 'professional',
};
// 직종 이름(화면 표시용, KSCO 대분류 이름을 줄인 것)
export const OCCUPATION_LABEL = { manager: '관리자', professional: '전문가', clerk: '사무', service: '서비스', sales: '판매',
  agri: '농림어업', craft: '기능원', machine: '장치·기계', labor: '단순노무' };
// 임금표에 없는 직업(자영업자, 군인)은 가장 가까운 직종으로 둔 게임 추정이다.
export const OCCUPATION_ESTIMATED = new Set(['owner', 'military']);

export const initEconomy = () => ({ cash: 0, mortgage: 0, mortgageAnnual: 0, other: 0, homeValue: 0, income: 0, spending: 0, series: [] });

// 관리자 직종은 임금근로자의 약 1%라 승진했다고 모두 관리자로 계산하지 않는다. 직업 자체의 직종을 쓴다.
export const occupationOf = (job) => (!job ? null : OCCUPATION_BY_JOB[job.id] ?? 'clerk');
const careerBucket = (years) => (years < 1 ? '1년미만' : years < 3 ? '1~3년미만' : years < 5 ? '3~5년미만' : years < 10 ? '5~10년미만' : '10년이상');
const decadeKey = (age) => (age < 30 ? '20s' : age < 40 ? '30s' : age < 50 ? '40s' : age < 60 ? '50s' : age < 70 ? '60s' : '70s');
/** 만원 단위 금액을 읽기 쉬운 문장으로 (예: 12345 → 1억 2,345만원) */
export const fmtMoney = (v) => {
  const n = Math.round(v), abs = Math.abs(n), sign = n < 0 ? '-' : '';
  if (abs >= 10000) { const eok = Math.floor(abs / 10000), rest = abs % 10000; return `${sign}${eok}억${rest ? ` ${rest.toLocaleString()}만원` : '원'}`; }
  return `${sign}${abs.toLocaleString()}만원`;
};

/** 물가지수: 2025년 = 1. 1960~2025는 소비자물가지수(세계은행), 그 밖은 가장 가까운 해를 잇고 2025년 이후는 연 2% 가정. 모든 금액은 2025년 값에 이 지수를 곱해 그해 금액으로 쓴다. */
export function priceIndex(year) {
  const t = getLifeTables().economy, ys = Object.keys(t.cpi).map(Number), min = Math.min(...ys), max = Math.max(...ys), base = t.cpi[t.cpiBaseYear];
  if (year > max) return (t.cpi[max] / base) * (1 + t.inflationAfter) ** (year - max);
  return t.cpi[Math.max(min, year)] / base;
}

/**
 * 소득지수: 2025년 = 1. 1인당 국민총소득(명목)의 변화로, 임금·집값·소비처럼 소득과 함께 움직이는 금액에 곱한다.
 * (소비자물가만으로 줄이면 과거의 임금이 실제보다 너무 높게 나와 집을 살 수 없게 된다. 실질 임금이 오른 만큼이 이 지수에 들어 있다.)
 */
export function incomeIndex(year) {
  const t = getLifeTables().economy, ys = Object.keys(t.gni).map(Number), min = Math.min(...ys), max = Math.max(...ys);
  if (year > max) return (t.gni[max] / t.gni[2025]) * (1 + t.incomeGrowthAfter) ** (year - max);
  return t.gni[Math.max(min, year)] / t.gni[2025];
}

/** 금액 문구의 {만원:숫자} 를 그해 가격으로 바꿔 쓴다(숫자는 2025년 값). 예: {만원:6298} → 1억 2,000만원 */
export const fillMoney = (text, s, age) => String(text ?? '').replace(/\{만원:([0-9.]+)\}/g, (all, n) => fmtMoney(Number(n) * incomeIndex(s.birthYear + age)));

/** 집값(만원): 2012~2025년은 한국부동산원 전국 평균 매매가, 그 앞뒤는 물가로 이어 간다. */
export const housePriceAt = (year) => {
  const p = getLifeTables().economy.housePrice, ys = Object.keys(p).map(Number), min = Math.min(...ys), max = Math.max(...ys);
  if (p[year] != null) return p[year];
  return year < min ? p[min] * incomeIndex(year) / incomeIndex(min) : p[max] * incomeIndex(year) / incomeIndex(max); // 집값은 소득과 함께 움직인다고 본다
};

/** 그 나이에 집을 살 때의 집값·대출·내 돈(만원). 대출은 연령대별 평균 신규취급액까지, 나머지는 현금에서 나간다. */
export function housePurchase(s, age) {
  const t = getLifeTables().economy, price = housePriceAt(s.birthYear + age), loan = Math.min(price, t.mortgageNew[decadeKey(age)] * incomeIndex(s.birthYear + age));
  return { price, loan, equity: price - loan };
}

/** 본인 연 소득(만원). 직업이 없거나 은퇴 뒤면 0. */
export function personalIncome(s, age) {
  const t = getLifeTables().economy;
  const eco = s.life?.economy;
  if (!s.job || age < WORK_START_AGE || age >= (eco?.workUntil ?? RETIRE_AGE) || eco?.retired) return 0;
  const occ = occupationOf(s.job), sex = s.birth?.gender === 'F' ? 'F' : 'M';
  const starts = [s.job.since, ...(s.jobHistory ?? []).map((j) => j.since ?? j.age)].filter(Number.isFinite);
  const years = age - (starts.length ? Math.min(...starts) : WORK_START_AGE);
  const mods = (eco?.incomeMods ?? []).filter((m) => age <= m.until).reduce((sum, m) => sum + m.pct, 0); // 장면 선택으로 생긴 소득 변화
  return Math.round(t.wage[occ][sex][careerBucket(years)] * Math.max(0, 1 + mods) * incomeIndex(s.birthYear + age)); // 그해 임금 수준
}

const curve = (points, x) => {
  if (x <= points[0][0]) return points[0][1]; // 1분위 평균 소득 이하는 1분위 소비지출로 둔다
  for (let i = 1; i < points.length; i++) {
    if (x <= points[i][0]) { const [x0, y0] = points[i - 1], [x1, y1] = points[i]; return y0 + (y1 - y0) * (x - x0) / (x1 - x0); }
  }
  const [x0, y0] = points[points.length - 2], [x1, y1] = points[points.length - 1];
  return y1 + (y1 - y0) * (x - x1) / (x1 - x0); // 5분위 위쪽은 같은 기울기로 이어 간다
};

export function householdSize(life, age) {
  const kids = (life.children ?? []).filter((c) => age - c.bornAge < 24).length;
  return Math.max(1, Math.min(5, 1 + (life.marriage?.status === 'married' ? 1 : 0) + kids));
}

const homeIndex = (fromYear, toYear) => housePriceAt(toYear) / housePriceAt(fromYear);
export const netWorth = (e) => Math.round(e.cash + e.homeValue - e.mortgage - e.other);

/**
 * 한 해 정산. 이미 갱신된 life(결혼·출산·이혼·주택 매입 반영)와 그 해 발생한 사건 종류(happened)를 받아 새 economy 를 돌려준다.
 * 순서: 이혼 재산 분할 → 집값 변동 → 주택 매입 → 소득·지출 → 상환 → 부족분 처리.
 */
export function stepEconomy(s, age, happened = []) {
  const t = getLifeTables().economy, life = s.life;
  const e = structuredClone(life.economy ?? initEconomy());
  const year = s.birthYear + age, f = incomeIndex(year), c = f; // 병원비·결혼 비용 같은 고정 금액도 소득 수준에 맞춰 같은 지수를 쓴다(소비자물가만 쓰면 과거에 비용이 소득보다 훨씬 무거워진다)
  if (age < WORK_START_AGE) {
    // 부모 가구에서 지내는 나이라 소득·지출은 계산하지 않는다. 이 나이에 집을 산 경우만 반영하고 자금은 부채로 둔다.
    if (happened.includes('house')) {
      const { price, loan } = housePurchase(s, age);
      e.homeValue += price; e.mortgage += loan; e.mortgageAnnual += loan / t.loanTermYears; e.other += price - loan;
    }
    return e;
  }
  if (happened.includes('divorce')) for (const k of ['cash', 'mortgage', 'mortgageAnnual', 'other', 'homeValue']) e[k] = e[k] / 2;
  if (e.homeValue > 0) e.homeValue = e.homeValue * homeIndex(year - 1, year);
  if (happened.includes('house')) {
    const { price, loan } = housePurchase(s, age);
    e.homeValue += price; e.mortgage += loan; e.mortgageAnnual += loan / t.loanTermYears; e.cash -= price - loan;
  }
  // 임금근로 상태(통계에서 만든 상태 모델)가 쉬는 중이면 그 해 본인 임금이 없다.
  if (age === WORK_START_AGE && !(e.series ?? []).length) e.cash += t.startNetWorth * f; // 시작 순자산(통계 중앙값에서 만든 값)
  // 임금근로가 아닌 해(상태 O)에는 임금 대신 자영·기타 소득이 임금의 일부(사업소득÷근로소득 중앙값)만큼 생긴다.
  const own = personalIncome(s, age) * (life.work?.state === 'O' ? (t.nonWageRatio ?? 0) : 1);
  const spouse = life.marriage?.status === 'married' ? own * t.spouseIncomeShare : 0;
  const pension = e.pension; // 연금 수령 장면에서 고른 시기와 배율(없으면 기본 규칙)
  // 은퇴 뒤 가구 소득은 통계 값이다: 60~64세(일을 하지 않을 때) retirementIncome, 65세 이후 seniorIncome(둘 다 재산소득은 따로 더한다). 연금 선택은 65세 이후 수준을 바꾼다.
  const transfer = (age >= t.publicTransferAge ? t.seniorIncome * (pension?.mult ?? 1) : own === 0 && age >= RETIRE_AGE ? t.retirementIncome : 0) * f;
  const property = Math.max(0, e.cash + e.homeValue) * t.propertyIncomeRate; // 재산소득(이자·임대 등)
  const income = own + spouse + transfer + property;
  const size = householdSize(life, age);
  // 소비: 소득으로 본 값과 가구원수로 본 값의 기하평균(두 통계가 같은 가구를 서로 다른 축으로 본 것이라 한쪽만 쓰면 한쪽 효과가 과장된다. 단순화)
  // 통계 값은 2025년 소득 수준의 값이라, 소득을 2025년 수준으로 되돌려 계산한 뒤 그해 수준으로 다시 곱한다.
  let spending = f * Math.sqrt(curve(t.consumptionCurve, income / f) * t.consumptionBySize[size - 1]);
  if (age >= t.publicTransferAge) spending = Math.min(spending, t.seniorConsumptionCap * f);
  const tax = (own + spouse) * t.taxShare;
  const interest = (e.mortgage + e.other) * t.interestRate;
  const transferOut = t.transferOut * f;
  const pet = (life.pet?.count ?? (life.pet?.owned ? 1 : 0)) * t.petAnnual * c; // 마리 수만큼 든다
  // 병원비: 고액 의료 상태면 연 본인부담이 든다. 그 해 치료 선택에서 이미 냈으면(medicalPaid) 또 내지 않고, 미룬 병원비(medicalDeferred)는 다음 해에 낸다.
  let medical = life.health?.state === 1 && e.medicalPaid !== age ? t.medicalOop * c : 0;
  if (e.medicalDeferred && age >= e.medicalDeferred.from) { medical += e.medicalDeferred.amount; e.medicalDeferred = null; }
  const principal = Math.min(e.mortgage, e.mortgageAnnual * t.amortizingShare);
  e.cash += income - tax - spending - transferOut - interest - pet - medical - principal;
  e.mortgage -= principal;
  if (e.mortgage < 1) { e.mortgage = 0; e.mortgageAnnual = 0; }
  if (e.cash > 0 && e.other > 0) { const pay = Math.min(e.cash, e.other); e.cash -= pay; e.other -= pay; }
  if (e.cash < 0 && e.homeValue > 0) { // 현금이 모자라면 집을 판다(게임 단순화)
    e.cash += e.homeValue - e.mortgage; e.homeValue = 0; e.mortgage = 0; e.mortgageAnnual = 0;
    if (e.cash > 0 && e.other > 0) { const pay = Math.min(e.cash, e.other); e.cash -= pay; e.other -= pay; }
  }
  if (e.cash < 0) { e.other += -e.cash; e.cash = 0; }
  e.income = Math.round(income); e.spending = Math.round(spending + tax + transferOut + interest + pet + medical);
  e.series = [...(e.series ?? []), { age, income: e.income, earned: Math.round(own + spouse), netWorth: netWorth(e), assets: Math.round(Math.max(0, e.cash) + e.homeValue), debt: Math.round(e.mortgage + e.other) }];
  return e;
}

// ── 결과 화면용: 그해 금액을 오늘(2026년) 돈 가치로 바꿔 보여 준다 ──
// 진행 중 기록(series)은 그해 가격 그대로 두고, 결과 화면에서만 같은 소득지수로 환산한다.
export const TODAY_YEAR = 2026;
export const todayFactor = (run, age) => incomeIndex(TODAY_YEAR) / incomeIndex(run.birthYear + age);
export const todayValue = (value, run, age) => (value == null ? value : value * todayFactor(run, age));
/** 경제 기록 한 해치를 오늘 가치로 바꾼 사본 */
export const todayPoint = (p, run) => {
  const f = todayFactor(run, p.age), conv = (v) => (v == null ? v : v * f);
  return { ...p, netWorth: conv(p.netWorth), income: conv(p.income), assets: conv(p.assets), debt: conv(p.debt) };
};
export const todaySeries = (run) => (run.life?.economy?.series ?? []).map((p) => todayPoint(p, run));
